import _toConsumableArray from "/builds/vistaprint-org/digital/acquisition/merchandising-components/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/builds/vistaprint-org/digital/acquisition/merchandising-components/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import * as React from 'react';
import { merge } from './merge.js';
import { omit } from './omit.js';
import { isNotProduction } from '../../../../core/utilities/env.utils.js';
import { useIsomorphicLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect.js';

function _define_property(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _object_spread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === "function") {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _define_property(target, key, source[key]);
    });
  }

  return target;
} // eslint-disable-next-line @typescript-eslint/naming-convention,no-underscore-dangle


var __DEV__ = isNotProduction(); //
// ─── HELPERS ────────────────────────────────────────────────────────────────────
//
// keep track of whether or not we have access to `window` (so that we don't crash during e.g. server-side rendering)


var windowExists = typeof window === 'object'; //
// ─── FACTORY ────────────────────────────────────────────────────────────────────
//

function createResponsiveSystem(screenClassConfiguration) {
  var defaultScreenClass = screenClassConfiguration.defaultScreenClass,
      breakpoints = screenClassConfiguration.breakpoints,
      _screenClassConfigura = screenClassConfiguration.cascadeMode,
      cascadeMode = _screenClassConfigura === void 0 ? 'no-cascade' : _screenClassConfigura; //
  // ─── VALIDATE ───────────────────────────────────────────────────────────────────
  //
  // optimize these error-checks out of production builds

  if (__DEV__) {
    // todo: validate that defaultScreenClass is defined or that breakpoints is an object?
    // how much should we assume based on our Types?
    var breakpointValues = Object.values(breakpoints);

    if (breakpointValues.length < 2) {
      throw new Error('ScreenClassConfigurationError - `breakpoints` must have at least 2 keys e.g. `{ mobile: 320, desktop: Infinity }`');
    }

    if (breakpointValues.filter(function (value) {
      return value === Infinity;
    }).length !== 1) {
      throw new Error('ScreenClassConfigurationError - `breakpoints` must have exactly 1 entry with a value of `Infinity` for the maximum pixel-width e.g. `{ mobile: 320, desktop: Infinity }`');
    }
  } //
  // ─── SORT ───────────────────────────────────────────────────────────────────────
  //
  // sort the screen classes from smallest -> largest (will make it easier to determine the proper screen class given a window-width later)


  var sortedScreenClassBreakpoints = Object.entries(breakpoints).sort(function (_ref, _ref2) {
    var _ref3 = _slicedToArray(_ref, 2),
        maxPixelWidth1 = _ref3[1];

    var _ref4 = _slicedToArray(_ref2, 2),
        maxPixelWidth2 = _ref4[1];

    return maxPixelWidth1 > maxPixelWidth2 ? 1 : 0;
  });
  var sortedScreenClasses = sortedScreenClassBreakpoints.map(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 1),
        screenClass = _ref6[0];

    return screenClass;
  });
  /**
  * Mobile-First Screen Classes include the current screen class and all smaller
  *
  * They should be applied smallest to largest
  */

  function getMobileFirstScreenClasses(breakpoint) {
    return sortedScreenClasses.slice(0, sortedScreenClasses.indexOf(breakpoint) + 1);
  }
  /**
  * Desktop-First Screen Classes include the current screen class and all larger
  *
  * They should be applied from largest to smallest
  */


  function getDesktopFirstScreenClasses(breakpoint) {
    return sortedScreenClasses.slice(sortedScreenClasses.indexOf(breakpoint)).reverse();
  } //
  // ─── CONTEXT ────────────────────────────────────────────────────────────────────
  //


  var screenClassContext = /*#__PURE__*/React.createContext(undefined);
  var screenClassInitializationContext = /*#__PURE__*/React.createContext(false); //
  // ─── PROVIDER ───────────────────────────────────────────────────────────────────
  //

  var Provider = screenClassContext.Provider;

  var ScreenClassProvider = function ScreenClassProvider(_ref7) {
    var children = _ref7.children;

    var _React$useState = React.useState(defaultScreenClass),
        _React$useState2 = _slicedToArray(_React$useState, 2),
        screenClass = _React$useState2[0],
        setScreenClass = _React$useState2[1];

    var _React$useState3 = React.useState(false),
        _React$useState4 = _slicedToArray(_React$useState3, 2),
        isInitialized = _React$useState4[0],
        setIsInitialized = _React$useState4[1];

    useIsomorphicLayoutEffect(function () {
      if (!windowExists) {
        return;
      } // build the media queries


      var screenClassMediaQueries = sortedScreenClassBreakpoints.map(function (_ref8, index) {
        var _ref9 = _slicedToArray(_ref8, 2),
            sortedScreenClass = _ref9[0],
            maxWidthPx = _ref9[1];

        // the minWidth for this screenClass is the maxWidth of the previous breakpoint + 1
        var minWidthPx = index > 0 ? sortedScreenClassBreakpoints[index - 1][1] + 1 : 0;
        var constraints = [];

        if (minWidthPx !== 0) {
          constraints.push("(min-width: ".concat(minWidthPx, "px)"));
        }

        if (maxWidthPx !== Infinity) {
          constraints.push("(max-width: ".concat(maxWidthPx, "px)"));
        }

        var mediaQuery = constraints.join(' and ');
        var mediaQueryList = window.matchMedia(mediaQuery); // in order to set the correct initial state, we need to immediately check each mql

        if (mediaQueryList.matches) {
          setScreenClass(sortedScreenClass);
          setIsInitialized(true);
        }

        return [sortedScreenClass, mediaQueryList];
      });
      var listeners = [];
      screenClassMediaQueries.forEach(function (_ref10) {
        var _ref11 = _slicedToArray(_ref10, 2),
            mediaScreenClass = _ref11[0],
            mediaQuery = _ref11[1];

        var listener = function listener(event) {
          if (event.matches) {
            setScreenClass(mediaScreenClass);
            setIsInitialized(true);
          }
        };

        mediaQuery.addListener(listener);
        listeners.push([mediaQuery, listener]);
      }); // eslint-disable-next-line consistent-return

      return function () {
        return listeners.forEach(function (_ref12) {
          var _ref13 = _slicedToArray(_ref12, 2),
              mql = _ref13[0],
              l = _ref13[1];

          return mql.removeListener(l);
        });
      };
    }, []);
    return /*#__PURE__*/React.createElement(screenClassInitializationContext.Provider, {
      value: isInitialized
    }, /*#__PURE__*/React.createElement(Provider, {
      value: screenClass
    }, children));
  };

  function useScreenClass() {
    var screenClass = React.useContext(screenClassContext);

    if (screenClass === undefined) {
      // optimize this error-check out of production builds
      if (__DEV__) {
        throw new Error("`useScreenClass` may only be used inside of a ScreenClassProvider. Make sure that you've rendered a ScreenClassProvider above this component your tree (usually folks render ScreenClassProvider near the root of their app). Returning the default screen class.");
      }

      return defaultScreenClass;
    }

    return screenClass;
  }

  function useIsScreenClassInitialized() {
    return React.useContext(screenClassInitializationContext);
  } // eslint-disable-next-line @typescript-eslint/ban-types


  function useResponsiveProps(props) {
    var currentScreenClass = React.useContext(screenClassContext);

    if (currentScreenClass === undefined) {
      // optimize this error-check out of production builds
      if (__DEV__) {
        throw new Error("`useResponsiveProps` may only be used inside of a ScreenClassProvider. Make sure that you've rendered a ScreenClassProvider above this component your tree (usually folks render ScreenClassProvider near the root of their app). Returning the default props with no overrides.");
      } // if there's no screenClassContext, we'll just return the default props with no overrides
      // TypeScript: this is correct, but TS is having trouble confirming that it will be type P


      return omit(props, sortedScreenClasses);
    } //
    // ─── DETERMINE PROPS ─────────────────────────────────────────────
    //
    // TypeScript: this is correct, but TS is having trouble confirming that it will be type P


    var baseProps = omit(props, sortedScreenClasses);
    var applicableScreenClasses = [];

    switch (cascadeMode) {
      case 'mobile-first':
        applicableScreenClasses = getMobileFirstScreenClasses(currentScreenClass);
        break;

      case 'desktop-first':
        applicableScreenClasses = getDesktopFirstScreenClasses(currentScreenClass);
        break;

      case 'no-cascade':
      default:
        applicableScreenClasses = [currentScreenClass];
    }

    var _props_sc; // apply each screen class on top of the baseProps
    // the screenClasses should be sorted in the order in which they should be applied
    // e.g. mobile-first should apply smallest -> largest
    // e.g. desktop-first should apply largest -> smallest
    // we assume that the sorting is already done


    var propsToMerge = [baseProps].concat(_toConsumableArray(applicableScreenClasses.map(function (sc) {
      return (_props_sc = props[sc]) !== null && _props_sc !== void 0 ? _props_sc : {};
    })));
    return merge(propsToMerge);
  } // Implementation - just forwardRef to everything
  // eslint-disable-next-line @typescript-eslint/ban-types


  function responsive(Component) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    var ResponsiveComponent = /*#__PURE__*/React.forwardRef(function (props, ref) {
      var responsiveProps = useResponsiveProps(props);
      return /*#__PURE__*/React.createElement(Component, _object_spread({
        ref: ref
      }, responsiveProps));
    });
    ResponsiveComponent.displayName = Component.displayName !== undefined ? "Responsive(".concat(Component.displayName, ")") : 'ResponsiveComponent';
    return ResponsiveComponent;
  }

  return {
    ScreenClassProvider: ScreenClassProvider,
    useResponsiveProps: useResponsiveProps,
    useScreenClass: useScreenClass,
    useIsScreenClassInitialized: useIsScreenClassInitialized,
    responsive: responsive
  };
}

export { createResponsiveSystem };